import React, { useState, useEffect, useCallback } from 'react';
import {
  DocumentTextIcon,
  PlusIcon,
  CloudArrowUpIcon,
  EyeIcon,
  TrashIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarDaysIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { documentsAPI } from '../../services/api';

const DocumentManagement = ({ employeeId, employeeName, canEdit = false, canViewAll = false }) => {
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploadData, setUploadData] = useState({
    file: null,
    documentTypeId: '',
    documentName: '',
    documentNumber: '',
    issueDate: '',
    expiryDate: '',
    issuingAuthority: '',
    issuingCountry: '',
    notes: ''
  });

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await documentsAPI.getEmployeeDocuments(employeeId);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  const loadDocumentTypes = useCallback(async () => {
    try {
      const response = await documentsAPI.getDocumentTypes();
      setDocumentTypes(response.data);
    } catch (error) {
      console.error('Error loading document types:', error);
    }
  }, []);

  useEffect(() => {
    if (employeeId) {
      loadDocuments();
      loadDocumentTypes();
    }
  }, [employeeId, loadDocuments, loadDocumentTypes]);

  const handleUpload = async () => {
    if (!uploadData.file || !uploadData.documentTypeId) {
      alert('Please select a file and document type');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('document', uploadData.file);
      formData.append('employeeId', employeeId);
      formData.append('documentTypeId', uploadData.documentTypeId);
      formData.append('documentName', uploadData.documentName || uploadData.file.name);
      formData.append('documentNumber', uploadData.documentNumber);
      formData.append('issueDate', uploadData.issueDate);
      formData.append('expiryDate', uploadData.expiryDate);
      formData.append('issuingAuthority', uploadData.issuingAuthority);
      formData.append('issuingCountry', uploadData.issuingCountry);
      formData.append('notes', uploadData.notes);

      await documentsAPI.uploadDocument(formData);
      setShowUploadModal(false);
      setUploadData({
        file: null,
        documentTypeId: '',
        documentName: '',
        documentNumber: '',
        issueDate: '',
        expiryDate: '',
        issuingAuthority: '',
        issuingCountry: '',
        notes: ''
      });
      await loadDocuments();
      alert('Document uploaded successfully');
    } catch (error) {
      console.error('Error uploading document:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to upload document';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    setLoading(true);
    try {
      await documentsAPI.updateDocument(selectedDocument.id, {
        documentName: selectedDocument.document_name,
        documentNumber: selectedDocument.document_number,
        issueDate: selectedDocument.issue_date,
        expiryDate: selectedDocument.expiry_date,
        issuingAuthority: selectedDocument.issuing_authority,
        issuingCountry: selectedDocument.issuing_country,
        notes: selectedDocument.notes,
        status: selectedDocument.status
      });
      setShowEditModal(false);
      setSelectedDocument(null);
      await loadDocuments();
      alert('Document updated successfully');
    } catch (error) {
      console.error('Error updating document:', error);
      alert('Failed to update document');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to archive this document?')) {
      return;
    }

    try {
      await documentsAPI.deleteDocument(documentId);
      await loadDocuments();
      alert('Document archived successfully');
    } catch (error) {
      console.error('Error archiving document:', error);
      alert('Failed to archive document');
    }
  };

  const handleDownload = async (documentId) => {
    try {
      const response = await documentsAPI.downloadDocument(documentId);
      // Trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document');
    }
  };

  const getExpiryStatusIcon = (status) => {
    switch (status) {
      case 'Expired':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      case 'Critical':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
      case 'Warning':
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      case 'Notice':
        return <CalendarDaysIcon className="w-5 h-5 text-blue-500" />;
      case 'Valid':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      default:
        return <DocumentTextIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getExpiryStatusColor = (status) => {
    switch (status) {
      case 'Expired':
        return 'bg-red-100 text-red-800';
      case 'Critical':
        return 'bg-red-100 text-red-800';
      case 'Warning':
        return 'bg-yellow-100 text-yellow-800';
      case 'Notice':
        return 'bg-blue-100 text-blue-800';
      case 'Valid':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Document Management</h3>
          <p className="text-sm text-gray-600">
            Manage {employeeName}'s documents, visas, and passports
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowUploadModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Upload Document
          </button>
        )}
      </div>

      {/* Documents List */}
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-8">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No documents</h3>
          <p className="mt-1 text-sm text-gray-500">
            No documents have been uploaded for this employee.
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {documents.map((document) => (
            <div
              key={document.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <DocumentTextIcon className="w-5 h-5 text-gray-500" />
                    <h4 className="font-medium text-gray-900">{document.document_name}</h4>
                    {getExpiryStatusIcon(document.expiry_status)}
                  </div>
                  
                  <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Type:</span>
                      <span className="ml-2 text-gray-900">{document.document_type_name}</span>
                    </div>
                    {document.document_number && (
                      <div>
                        <span className="text-gray-500">Number:</span>
                        <span className="ml-2 text-gray-900">{document.document_number}</span>
                      </div>
                    )}
                    {document.issue_date && (
                      <div>
                        <span className="text-gray-500">Issue Date:</span>
                        <span className="ml-2 text-gray-900">
                          {new Date(document.issue_date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {document.expiry_date && (
                      <div>
                        <span className="text-gray-500">Expiry Date:</span>
                        <span className="ml-2 text-gray-900">
                          {new Date(document.expiry_date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {document.issuing_authority && (
                      <div>
                        <span className="text-gray-500">Issuing Authority:</span>
                        <span className="ml-2 text-gray-900">{document.issuing_authority}</span>
                      </div>
                    )}
                    {document.issuing_country && (
                      <div>
                        <span className="text-gray-500">Country:</span>
                        <span className="ml-2 text-gray-900">{document.issuing_country}</span>
                      </div>
                    )}
                  </div>

                  {document.expiry_status !== 'No expiry' && (
                    <div className="mt-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getExpiryStatusColor(document.expiry_status)}`}>
                        {document.expiry_status}
                        {document.days_until_expiry !== null && document.days_until_expiry >= 0 && 
                          ` (${document.days_until_expiry} days)`
                        }
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => handleDownload(document.id)}
                    className="p-2 text-gray-400 hover:text-gray-600"
                    title="Download"
                  >
                    {/* eslint-disable-next-line */}
                    <ArrowDownTrayIcon className="w-4 h-4" />
                  </button>
                  {canEdit && (
                    <>
                      <button
                        onClick={() => {
                          setSelectedDocument(document);
                          setShowEditModal(true);
                        }}
                        className="p-2 text-gray-400 hover:text-gray-600"
                        title="Edit"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(document.id)}
                        className="p-2 text-gray-400 hover:text-red-600"
                        title="Archive"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl bg-white rounded-md shadow-lg">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Upload Document</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Document Type *</label>
                <select
                  value={uploadData.documentTypeId}
                  onChange={(e) => setUploadData({...uploadData, documentTypeId: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select document type</option>
                  {documentTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">File *</label>
                <input
                  type="file"
                  onChange={(e) => setUploadData({...uploadData, file: e.target.files[0]})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Document Name</label>
                  <input
                    type="text"
                    value={uploadData.documentName}
                    onChange={(e) => setUploadData({...uploadData, documentName: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Auto-filled from filename"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Document Number</label>
                  <input
                    type="text"
                    value={uploadData.documentNumber}
                    onChange={(e) => setUploadData({...uploadData, documentNumber: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Issue Date</label>
                  <input
                    type="date"
                    value={uploadData.issueDate}
                    onChange={(e) => setUploadData({...uploadData, issueDate: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Expiry Date</label>
                  <input
                    type="date"
                    value={uploadData.expiryDate}
                    onChange={(e) => setUploadData({...uploadData, expiryDate: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Issuing Authority</label>
                  <input
                    type="text"
                    value={uploadData.issuingAuthority}
                    onChange={(e) => setUploadData({...uploadData, issuingAuthority: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Issuing Country</label>
                  <input
                    type="text"
                    value={uploadData.issuingCountry}
                    onChange={(e) => setUploadData({...uploadData, issuingCountry: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  value={uploadData.notes}
                  onChange={(e) => setUploadData({...uploadData, notes: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadData({
                    file: null,
                    documentTypeId: '',
                    documentName: '',
                    documentNumber: '',
                    issueDate: '',
                    expiryDate: '',
                    issuingAuthority: '',
                    issuingCountry: '',
                    notes: ''
                  });
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedDocument && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl bg-white rounded-md shadow-lg">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Edit Document</h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Document Name</label>
                  <input
                    type="text"
                    value={selectedDocument.document_name}
                    onChange={(e) => setSelectedDocument({...selectedDocument, document_name: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Document Number</label>
                  <input
                    type="text"
                    value={selectedDocument.document_number || ''}
                    onChange={(e) => setSelectedDocument({...selectedDocument, document_number: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Issue Date</label>
                  <input
                    type="date"
                    value={selectedDocument.issue_date ? selectedDocument.issue_date.split('T')[0] : ''}
                    onChange={(e) => setSelectedDocument({...selectedDocument, issue_date: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Expiry Date</label>
                  <input
                    type="date"
                    value={selectedDocument.expiry_date ? selectedDocument.expiry_date.split('T')[0] : ''}
                    onChange={(e) => setSelectedDocument({...selectedDocument, expiry_date: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Issuing Authority</label>
                  <input
                    type="text"
                    value={selectedDocument.issuing_authority || ''}
                    onChange={(e) => setSelectedDocument({...selectedDocument, issuing_authority: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Issuing Country</label>
                  <input
                    type="text"
                    value={selectedDocument.issuing_country || ''}
                    onChange={(e) => setSelectedDocument({...selectedDocument, issuing_country: e.target.value})}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={selectedDocument.status}
                  onChange={(e) => setSelectedDocument({...selectedDocument, status: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                  <option value="replaced">Replaced</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  value={selectedDocument.notes || ''}
                  onChange={(e) => setSelectedDocument({...selectedDocument, notes: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedDocument(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEdit}
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentManagement;